/**
 * 子账户管理服务
 */
import { apiRequest } from '@/utils/api'

class SubAccountService {
  /**
   * 获取子账户列表
   * @param {Object} params - 查询参数 
   * @returns {Promise}
   */
  async getSubAccounts(params = {}) {
    try {
      const response = await apiRequest.get('/api/accounts/v2/sub-accounts/', params)
      return response.data
    } catch (error) {
      console.error('获取子账户列表失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取子账户详情
   * @param {number} accountId - 子账户ID
   * @returns {Promise}
   */
  async getSubAccount(accountId) {
    try {
      const response = await apiRequest.get(`/api/accounts/v2/sub-accounts/${accountId}/`)
      return response.data
    } catch (error) {
      console.error('获取子账户详情失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 创建子账户
   * @param {Object} data - 子账户数据
   * @returns {Promise}
   */
  async createSubAccount(data) {
    try {
      const response = await apiRequest.post('/api/accounts/v2/sub-accounts/', data)
      return response.data
    } catch (error) {
      console.error('创建子账户失败:', error)
      // 直接抛出原始错误，保持完整的response属性
      throw error
    }
  }

  /**
   * 更新子账户基本信息
   * @param {number} accountId - 子账户ID
   * @param {Object} data - 更新数据
   * @returns {Promise}
   */
  async updateSubAccount(accountId, data) {
    try {
      const response = await apiRequest.patch(`/api/accounts/v2/sub-accounts/${accountId}/`, data)
      return response.data
    } catch (error) {
      console.error('更新子账户失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 删除子账户
   * @param {number} accountId - 子账户ID
   * @returns {Promise}
   */
  async deleteSubAccount(accountId) {
    try {
      await apiRequest.delete(`/api/accounts/v2/sub-accounts/${accountId}/`)
      return { success: true }
    } catch (error) {
      console.error('删除子账户失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 切换子账户状态（启用/禁用）
   * @param {number} accountId - 子账户ID
   * @returns {Promise}
   */
  async toggleAccountStatus(accountId) {
    try {
      const response = await apiRequest.post(`/api/accounts/v2/sub-accounts/${accountId}/toggle-status/`)
      return response.data
    } catch (error) {
      console.error('切换子账户状态失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取子账户权限
   * @param {number} accountId - 子账户ID
   * @returns {Promise}
   */
  async getSubAccountPermissions(accountId) {
    try {
      const response = await apiRequest.get(`/api/accounts/v2/sub-accounts/${accountId}/permissions/`)
      return response.data
    } catch (error) {
      console.error('获取子账户权限失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 更新子账户权限
   * @param {number} accountId - 子账户ID
   * @param {Object} permissions - 权限数据
   * @returns {Promise}
   */
  async updateSubAccountPermissions(accountId, permissions) {
    try {
      const response = await apiRequest.put(`/api/accounts/v2/sub-accounts/${accountId}/permissions/`, permissions)
      return response.data
    } catch (error) {
      console.error('更新子账户权限失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取配额信息
   * @returns {Promise}
   */
  async getQuota() {
    try {
      const response = await apiRequest.get('/api/accounts/v2/sub-accounts/quota/')
      return response.data
    } catch (error) {
      console.error('获取配额信息失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取可分配的资源列表
   * @returns {Promise}
   */
  async getAvailableResources() {
    try {
      const response = await apiRequest.get('/api/accounts/v2/sub-accounts/available-resources/')
      return response.data
    } catch (error) {
      console.error('获取可用资源失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取子账户活动日志
   * @param {Object} params - 查询参数
   * @returns {Promise}
   */
  async getActivityLogs(params = {}) {
    try {
      const response = await apiRequest.get('/api/accounts/v2/sub-accounts/activity-logs/', { params })
      return response.data
    } catch (error) {
      console.error('获取活动日志失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取主账户信息
   * @returns {Promise}
   */
  async getMasterAccountInfo() {
    try {
      const response = await apiRequest.get('/api/accounts/v2/master-account/')
      return response.data.results?.[0] || response.data
    } catch (error) {
      console.error('获取主账户信息失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 获取主账户统计信息
   * @returns {Promise}
   */
  async getMasterAccountStats() {
    try {
      const response = await apiRequest.get('/api/accounts/v2/master-account/stats/')
      return response.data
    } catch (error) {
      console.error('获取主账户统计失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 批量导入子账户
   * @param {File} file - CSV文件
   * @returns {Promise}
   */
  async batchImportSubAccounts(file) {
    try {
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await apiRequest.post('/api/accounts/v2/sub-accounts/batch-import/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error) {
      console.error('批量导入子账户失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 导出子账户列表
   * @param {Object} params - 导出参数
   * @returns {Promise}
   */
  async exportSubAccounts(params = {}) {
    try {
      const response = await apiRequest.get('/api/accounts/v2/sub-accounts/export/', {
        params,
        responseType: 'blob'
      })
      
      // 创建下载链接
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `sub_accounts_${new Date().toISOString().split('T')[0]}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      return { success: true }
    } catch (error) {
      console.error('导出子账户列表失败:', error)
      throw this._handleError(error)
    }
  }

  /**
   * 处理错误响应
   * @param {Error} error - 错误对象
   * @returns {Error}
   * @private
   */
  _handleError(error) {
    if (error.response) {
      // 服务器返回错误状态码
      const { status, data } = error.response
      
      // 对于400错误（验证错误），保留原始错误响应以便前端处理字段级错误
      if (status === 400 && data.error_data) {
        const newError = new Error(data.exception_message || '请求参数错误')
        // 重要：保留完整的response属性
        newError.response = error.response
        newError.status = status
        newError.data = data
        return newError
      }
      
      switch (status) {
        case 400:
          return new Error(data.error || data.detail || '请求参数错误')
        case 401:
          return new Error('未授权访问，请重新登录')
        case 403:
          return new Error(data.error || data.detail || '权限不足，无法执行此操作')
        case 404:
          return new Error('请求的资源不存在')
        case 409:
          return new Error(data.error || '操作冲突，请重试')
        case 429:
          return new Error('请求过于频繁，请稍后再试')
        case 500:
          return new Error('服务器内部错误，请稍后重试')
        default:
          return new Error(data.error || data.detail || `请求失败 (${status})`)
      }
    } else if (error.request) {
      // 网络错误
      return new Error('网络连接失败，请检查网络设置')
    } else {
      // 其他错误
      return new Error(error.message || '未知错误')
    }
  }
}

// 导出单例实例
export const subAccountService = new SubAccountService()
export default subAccountService
// Cache bust: Wed Sep 17 04:39:19 UTC 2025
